/**
 * Shared dashed drag-and-drop file picker (UI/UX VC-E). The Snapshots tab had
 * a polished drop-zone while Replay used a raw native <input type=file> that
 * clashed with the dark theme; both now use this. Calls `onFile` for a drop or
 * a picked file and resets the input so the same file can be re-selected.
 */
import { useRef, useState } from 'react';

interface FileDropZoneProps {
  readonly accept: string;
  readonly onFile: (file: File) => void;
  /** Text shown before the "pick a file" link. */
  readonly prompt?: string;
  /** Test id for the drop-zone container. */
  readonly testId?: string;
  /** Test id for the "pick a file" button. */
  readonly pickTestId?: string;
  /** Test id for the hidden <input type=file> (Playwright setInputFiles target). */
  readonly inputTestId?: string;
}

export function FileDropZone({
  accept,
  onFile,
  prompt = 'Drop a file here, or',
  testId,
  pickTestId,
  inputTestId,
}: FileDropZoneProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files.item(0);
        if (file) onFile(file);
      }}
      className={
        dragOver
          ? 'rounded border-2 border-dashed border-primary-base bg-primary-pressed/20 p-4 text-center text-xs text-primary-on'
          : 'rounded border-2 border-dashed border-slate-600 bg-slate-950/40 p-4 text-center text-xs text-slate-400'
      }
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      {prompt}{' '}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="underline hover:text-primary-soft focus:outline-none focus:ring-2 focus:ring-primary-base"
        {...(pickTestId !== undefined ? { 'data-testid': pickTestId } : {})}
      >
        pick a file
      </button>
      .
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.item(0);
          if (file) onFile(file);
          e.target.value = ''; // allow re-selecting the same file
        }}
        {...(inputTestId !== undefined ? { 'data-testid': inputTestId } : {})}
      />
    </div>
  );
}
